<template>
  <div class="template-container">
    <div
      class="list-container"
      :class="{ 'sidebar-displayed': isTypographySidebarDisplayed || isColorSidebarDisplayed }"
    >
      <div class="container-header">
        <div class="brand-heading">Brand Identity</div>

        <div class="brand-second-row">
          <div class="heading-row right-section">
            <div class="left">
              <label>Brand Colors</label>
            </div>

            <div class="right">
              <div class="add-scheme" @click="openAddColorModal">
                <i class="material-icons-outlined align-middle button add"> add </i>
              </div>
            </div>
          </div>

          <div class="heading-row left-section">
            <div class="left">
              <label>Brand Typography</label>
            </div>

            <div class="right">
              <div class="add-scheme" @click="openTypographyModal">
                <i class="material-icons-outlined align-middle button add"> add </i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="container-body">
        <div class="colors-container">
          <div v-if="organisationSchemes.color.length > 0" class="schemes-list">
            <div
              class="scheme-item color-scheme-item"
              v-for="scheme in organisationSchemes.color"
              :key="scheme.id"
              :class="{ selected: isSelected(scheme.id) }"
              @click="handleColorClick(scheme)"
            >
              <div class="rows">
                <div class="item-header">{{ scheme.name }}</div>

                <div class="item-dropdown dropdown">
                  <i
                    class="material-icons-outlined align-middle more-buttons dropdown-toggle"
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                  >
                    more_vert
                  </i>

                  <div class="dropdown-menu dropdown-menu-right">
                    <!-- vshow -->
                    <button
                      type="button"
                      class="dropdown-item"
                      @click="openDeleteSchemeModal(scheme)"
                    >
                      <i class="material-icons-outlined align-middle">delete</i>
                      Delete
                    </button>
                  </div>
                </div>
              </div>

              <div class="item-details">
                <div class="input-group-prepend">
                  <span class="" :style="{ backgroundColor: scheme.config['color'] }"></span>
                </div>
                <div class="color-code">{{ scheme.config['color'] }}</div>
              </div>
            </div>
          </div>

          <div v-else class="empty-list">
            <div class="empty-list-message">To add brand colors, click the plus sign.</div>
          </div>
        </div>
        <div class="fonts-container">
          <div v-if="organisationSchemes.typography.length > 0" class="schemes-list">
            <div
              class="scheme-item"
              v-for="scheme in organisationSchemes.typography"
              :key="scheme.id"
              :class="{ selected: isSelected(scheme.id) }"
              @click="handleTypographyClick(scheme)"
            >
              <div class="rows">
                <div class="item-header">{{ scheme.name }}</div>

                <div class="item-dropdown dropdown">
                  <i
                    class="material-icons-outlined align-middle more-buttons dropdown-toggle"
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                  >
                    more_vert
                  </i>

                  <div class="dropdown-menu dropdown-menu-right">
                    <button
                      type="button"
                      class="dropdown-item"
                      @click.stop="openDeleteSchemeModal(scheme)"
                    >
                      <i class="material-icons-outlined align-middle">delete</i>
                      Delete
                    </button>
                  </div>
                </div>

                <div class="left">
                  <span class="font-label">Font: </span>
                  <span :style="{ fontFamily: scheme.config['font-family'] }">{{
                    scheme.config['font-family']
                  }}</span>
                </div>

                <!-- <div class="right">
                  <span class="font-label">Text Size:</span>
                  {{ scheme.config['font-size'] }}
                </div> -->
              </div>
            </div>
          </div>
          <div v-else class="empty-list">
            <div class="empty-list-message">To add brand typography, click the plus sign.</div>
          </div>
        </div>
      </div>
    </div>

    <AddColorModal
      v-if="isAddColorModalOpen"
      @close="isAddColorModalOpen = false"
      @schemeAdded="displayNewScheme"
    />
    <AddTypographyModal
      v-if="isAddTypographyModalOpen"
      @close="isAddTypographyModalOpen = false"
      @schemeAdded="displayNewScheme"
    />
    <DeleteSchemeModal
      v-if="showDeleteSchemeModal"
      @close="showDeleteSchemeModal = false"
      :scheme="selectedScheme"
      @deleted="fetchOrganisationSchemes"
    />
    <TypographySidebar
      v-if="isTypographySidebarDisplayed"
      :scheme="selectedScheme"
      @close="isTypographySidebarDisplayed = false"
      @update="fetchOrganisationSchemes"
    />
    <ColorSidebar
      v-if="isColorSidebarDisplayed"
      :scheme="selectedScheme"
      @close="isColorSidebarDisplayed = false"
      @update="fetchOrganisationSchemes"
    />
  </div>
</template>

<script>
  import AddColorModal from './AddColorModal.vue';
  import AddTypographyModal from './AddTypographyModal.vue';
  import DeleteSchemeModal from './DeleteSchemeModal.vue';
  import TypographySidebar from './TypographySidebar.vue';
  import ColorSidebar from './ColorSidebar.vue';
  import { apiGetOrganisationScheme, apiGetUserOrganisationScheme } from '@/api/organisation';

  export default {
    name: 'AppearanceScheme',
    props: {
      organisation: Object,
    },
    components: {
      AddColorModal,
      AddTypographyModal,
      DeleteSchemeModal,
      TypographySidebar,
      ColorSidebar,
    },
    data() {
      return {
        organisationSchemes: {
          color: [],
          typography: [],
        },
        isAddColorModalOpen: false,
        isAddTypographyModalOpen: false,
        selectedScheme: null,
        showDeleteSchemeModal: false,
        isTypographySidebarDisplayed: false,
        isColorSidebarDisplayed: false,
      };
    },
    computed: {
      organisationIdParam() {
        return this.$route.params.organisation_id;
      },
      isAdmin() {
        return this.$store.getters.isAdministrator;
      },
    },
    mounted() {
      this.fetchOrganisationSchemes();
    },
    methods: {
      async fetchOrganisationSchemes() {
        const orgID = this.organisationIdParam;
        let response;

        if (this.isAdmin) {
          response = await apiGetOrganisationScheme(orgID);
        } else {
          response = await apiGetUserOrganisationScheme();
        }

        this.organisationSchemes = {
          color: response.data.color || [],
          typography: response.data.typography || [],
        };

        this.isTypographySidebarDisplayed = false;
        this.isColorSidebarDisplayed = false;
      },
      openAddColorModal() {
        this.isAddColorModalOpen = true;
      },
      openTypographyModal() {
        this.isAddTypographyModalOpen = true;
      },
      displayNewScheme(newScheme) {
        if (newScheme.type === 'color') {
          this.organisationSchemes.color.push(newScheme);
        } else if (newScheme.type === 'typography') {
          this.organisationSchemes.typography.push(newScheme);
        }
      },
      setSelectedScheme(scheme = null) {
        this.selectedScheme = scheme;
      },
      handleTypographyClick(scheme) {
        // TODO: implement
        return;
        if (scheme === this.selectedScheme) {
          this.isTypographySidebarDisplayed = !this.isTypographySidebarDisplayed;
        } else {
          this.selectedScheme = scheme;
          this.isTypographySidebarDisplayed = true;
        }
        this.isColorSidebarDisplayed = false;
      },
      handleColorClick(scheme) {
        if (scheme === this.selectedScheme) {
          this.isColorSidebarDisplayed = !this.isColorSidebarDisplayed;
        } else {
          this.selectedScheme = scheme;
          this.isColorSidebarDisplayed = true;
        }
        this.isTypographySidebarDisplayed = false;
      },
      isSelected(schemeId) {
        return this.selectedScheme?.id === schemeId;
      },
      openDeleteSchemeModal(scheme) {
        this.setSelectedScheme(scheme);
        this.showDeleteSchemeModal = true;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .template-container {
    display: flex;
    height: 100%;
  }

  .list-container {
    gap: 0px;
    width: 100%;
    height: 100%;
    border-top: solid 2px $lineGrey;
    padding-top: 20px;

    &.sidebar-displayed {
      width: 80%;
    }
  }

  .container-header {
    font-family: Poppins;
    font-weight: 400;
    font-size: 20px;
    line-height: 32px;
    letter-spacing: 0%;
    height: 92px;
  }

  .brand-second-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0px;
    padding-right: 10px;

    .right-section {
      padding-right: 20px;
      padding-right: 20px;
      padding-top: 20px;
      border-right: solid 2px $lineGrey;
    }

    .left-section {
      padding-left: 20px;
      padding-top: 20px;
      padding-right: 20px;
    }
  }

  .container-body {
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding-right: 10px;
    height: 100%;
    overflow-y: hidden;
  }

  .colors-container {
    border-right: solid 2px $lineGrey;
    padding-right: 20px;
    padding-top: 10px;
    padding-bottom: 20px;
    overflow-y: auto;
  }

  .heading-row {
    display: grid;
    grid-template-columns: 1fr 24px;
    gap: 0px;
    padding-bottom: 10px;
  }

  .brand-heading {
    font-family: Poppins;
    font-weight: 400;
    font-size: 20px;
    line-height: 32px;
    letter-spacing: 0%;
  }

  label {
    font-size: 14px;
  }

  .fonts-container {
    padding-bottom: 20px;
    padding-left: 20px;
    padding-top: 10px;
    padding-right: 16px;
    overflow-y: auto;
  }

  .schemes-list {
    display: grid;
    gap: 8px;
  }

  .empty-list {
    height: 100%;
    display: flex;
    text-align: center;

    .empty-list-message {
      margin: auto;
      color: $secondaryText;
      font-weight: 400;
    }
  }
  .scheme-item {
    background-color: #ffffff;
    padding: 16px;
    border: solid 2px transparent;
    height: 88px;
    overflow: hidden;

    &:hover {
      border: 2px solid $primaryRed;
    }
  }

  .selected {
    border: 2px solid $primaryRed;
  }

  .color-scheme-item {
    display: grid;
    gap: 10px;
  }

  .item-header {
    font-family: Poppins;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
  }

  .color-code {
    font-family: Tahoma;
    font-weight: 400;
    font-size: 14px;
    line-height: 100%;
    vertical-align: middle;
  }

  .rows {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    font-family: Poppins;
    font-weight: 400;
    font-size: 14px;
    line-height: 100%;
    letter-spacing: 0%;
  }

  .item-dropdown {
    display: flex;
    justify-content: flex-end;
  }

  .font-label {
    color: $secondaryText;
  }

  .dropdown {
    display: flex;
    justify-content: flex-end;
  }

  .add:hover {
    color: $primaryRed;
  }

  .input-group-prepend span {
    width: 18px;
    height: 18px;
    border-radius: 4px;
    box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.25);
  }

  .item-details {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
  }
</style>
